import {useMemo, useState} from 'react';
import {
  Divider,
  FormControl,
  Menu,
  MenuItem,
  TextField,
  Stack,
  Typography,
  InputBase,
  InputAdornment,
  FormControlLabel,
  Switch,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DateRangeIcon from '@mui/icons-material/DateRange';
import SearchIcon from '@mui/icons-material/Search';
import DriveFileRenameOutlineRoundedIcon from '@mui/icons-material/DriveFileRenameOutlineRounded';
import {FishIcon} from 'styles/icons';
import {useTranslation} from 'react-i18next';
import {useGetSmoltSupplies} from 'hooks/swr';
import NumberFormat from 'react-number-format';
import type {SmoltSupplyEnrichedResponse} from 'client';
import {getThousandsSeparator, getDecimalSeparator} from 'helpers/NumberFormatter';
import {dateReader, kilosToGrams} from 'helpers/UnitsFormatHandler';
import {isDateBetween} from 'helpers/dateUtils';

interface SmoltSupplySelectorProps {
  planId: string;
  smoltSupplyId: string | null;
  onChange: (smoltSupplyId: string | null) => void;
  selectedDates?: Date[];
}

const MAX_HEIGHT: string = '400px';
const MAX_WIDTH: string = '262px';
const MENU_MIN_WIDTH: string = '320px';

export const SmoltSupplySelector = ({
  planId,
  smoltSupplyId,
  onChange,
  selectedDates,
}: SmoltSupplySelectorProps): JSX.Element => {
  const {t} = useTranslation('common');
  const {data: smoltSupplies} = useGetSmoltSupplies({resourceId: planId});
  const [filterByDate, setFilterByDate] = useState<boolean>(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleSmoltChange = (id: string | null): void => {
    onChange(id);
    handleClose();
  };
  const handleToggleDateFilter = ({target: {checked}}: React.ChangeEvent<HTMLInputElement>) => {
    setFilterByDate(checked);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const filteredSmoltSupplies = useMemo((): SmoltSupplyEnrichedResponse[] => {
    if (!smoltSupplies) {
      return [];
    }
    const datesToFilter = selectedDates ?? [];

    return smoltSupplies
      .filter((smoltSupply) => {
        const matchesSearch = smoltSupply.name?.toLowerCase().includes(searchValue.toLowerCase()) ?? false;
        const matchesDate =
          datesToFilter.length === 0 ||
          !filterByDate ||
          datesToFilter.some((selectedDateStr) => {
            const start = dateReader(smoltSupply.startDate);
            const end = dateReader(smoltSupply.endDate);
            const selectedDate = dateReader(selectedDateStr);

            return isDateBetween(selectedDate, start, end);
          });

        return matchesSearch && matchesDate;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [smoltSupplies, selectedDates, searchValue, filterByDate]);

  const smoltBatchesToUpdate = filteredSmoltSupplies;

  return (
    <>
      {smoltSupplies && (
        <FormControl fullWidth>
          <TextField
            label={t('smoltSupply')}
            value={smoltSupplyId}
            onClick={handleClick}
            sx={{width: '100%'}}
            slotProps={{
              input: {
                sx: {minHeight: smoltSupplyId ? 64 : 54, paddingRight: 1},
                endAdornment: <ArrowDropDownIcon />,
                inputComponent: () => (
                  <SmoltSupplyMenuItem
                    key={smoltSupplyId}
                    smoltSupply={smoltSupplyIdToSmoltSupply(smoltSupplyId ?? '', smoltSupplies)}
                    handleSmoltChange={() => {}}
                    paddingY={1}
                  />
                ),
              },
              inputLabel: {shrink: true},
            }}
          />
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            slotProps={{
              list: {
                sx: {
                  maxHeight: MAX_HEIGHT,
                  minWidth: MENU_MIN_WIDTH,
                  maxWidth: MAX_WIDTH,
                  paddingTop: 0,
                },
              },
            }}
            autoFocus={false}
            disableAutoFocusItem={true}
          >
            <InputBase
              fullWidth
              value={searchValue}
              onChange={handleSearchChange}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                e.stopPropagation();
              }}
              sx={{
                px: 2,
                py: 1,
                backgroundColor: (theme) => theme.palette.background.default,
              }}
              placeholder={t('search')}
              autoFocus
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon fontSize="inherit" color="secondary" />
                </InputAdornment>
              }
            />
            <Divider sx={{my: '0px !important', opacity: 0.4}} />
            <Stack sx={{pl: 3, py: 1.5}}>
              <FormControlLabel
                control={<Switch size="small" checked={filterByDate} onChange={handleToggleDateFilter} />}
                label={
                  <Typography typography={'body2'} noWrap pl={1} sx={{userSelect: 'none'}}>
                    {t('filterSmoltBatchesByDate')}
                  </Typography>
                }
              />
            </Stack>
            <Divider sx={{my: '0px !important'}} />
            {smoltBatchesToUpdate.map((smoltSupply) => (
              <SmoltSupplyMenuItem
                key={smoltSupply.id}
                smoltSupply={smoltSupply}
                handleSmoltChange={handleSmoltChange}
                divider={true}
              />
            ))}
            <MenuItem
              sx={{px: 5, py: 2}}
              onClick={() => {
                handleSmoltChange(null);
              }}
            >
              <Typography typography={'body1'} noWrap>
                {t('noneSelected')}
              </Typography>
            </MenuItem>
          </Menu>
        </FormControl>
      )}
    </>
  );
};

const smoltSupplyIdToSmoltSupply = (
  id: string,
  smoltSupplies: SmoltSupplyEnrichedResponse[],
): SmoltSupplyEnrichedResponse | undefined => {
  return smoltSupplies?.find((smoltSupply) => smoltSupply.id === id);
};

const SmoltSupplyMenuItem = ({
  smoltSupply,
  handleSmoltChange,
  divider = false,
  paddingY = 0.5,
}: {
  smoltSupply: SmoltSupplyEnrichedResponse | undefined;
  handleSmoltChange: (id: string) => void;
  divider?: boolean;
  paddingY?: number;
}) => {
  const {t} = useTranslation('common');

  if (!smoltSupply) {
    return (
      <Stack width="100%" pl={2}>
        <Typography typography={'body1'} noWrap>
          {t('smoltSupply')}
        </Typography>
      </Stack>
    );
  }

  const availableCount: number = Math.round(smoltSupply.count - smoltSupply.stockedCount);

  return (
    <MenuItem
      value={smoltSupply.id}
      divider={divider}
      sx={{width: '100%'}}
      onClick={() => {
        handleSmoltChange(smoltSupply.id);
      }}
    >
      <Stack
        direction="column"
        paddingY={paddingY}
        paddingRight={0}
        spacing={0.2}
        alignItems="center"
        width="100%"
        justifyContent={'space-between'}
      >
        <Stack direction="row" spacing={1} alignItems="center" width="100%">
          <Stack direction="row" spacing={1} alignItems="center" overflow={'hidden'} textOverflow={'ellipsis'}>
            <Stack direction="row" width={16}>
              <DriveFileRenameOutlineRoundedIcon fontSize="inherit" color="secondary" />
            </Stack>
            <Typography typography={'body3'} noWrap>
              {smoltSupply.name}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" width="100%">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Stack direction="row" width={16}>
                <DateRangeIcon fontSize="inherit" color="secondary" />
              </Stack>
              <Typography typography={'body3'} noWrap>
                {`${smoltSupply.startDate} → ${smoltSupply.endDate}`}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" width="100%">
          <Stack direction="row" spacing={0.5} alignItems="center" width="100%">
            <Stack direction="row" width={20}>
              <FishIcon fontSize="inherit" color="secondary" />
            </Stack>
            <Typography typography={'body3'} color="secondary">{`${t('remainingCount')}: `}</Typography>
            <Typography typography={'body3'} color={availableCount < 0 ? 'error.main' : 'text.primary'} noWrap>
              <NumberFormat
                value={availableCount}
                displayType={'text'}
                thousandSeparator={getThousandsSeparator()}
                isNumericString
                decimalSeparator={getDecimalSeparator()}
              />
              {' / '}
              <NumberFormat
                value={smoltSupply.count}
                displayType={'text'}
                thousandSeparator={getThousandsSeparator()}
                isNumericString
                decimalSeparator={getDecimalSeparator()}
              />
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" width="100%">
          <Stack direction="row" spacing={0.5} alignItems="center" width="100%">
            <Stack direction="row" width={20}></Stack>
            <Typography typography={'body3'} color="secondary">{`${t('weight')}: `}</Typography>
            <Typography typography={'body3'} noWrap>
              {kilosToGrams(smoltSupply.weight)}
            </Typography>
            <Typography typography={'body3'} color="secondary">
              {t('gram')}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </MenuItem>
  );
};
